import { useState, useEffect, useRef, useMemo } from 'react';
import { RefreshCw, CheckCircle, Eye, EyeOff, FolderOpen, Loader2, FileWarning, RotateCcw, Trash2, Play, Square, Clock, XCircle, Search, Users, FileText, Building2, User, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_COLORS = {
  laeuft: 'bg-blue-500',
  fertig: 'bg-emerald-500',
  fehler: 'bg-red-500',
  wartend: 'bg-slate-300',
  abgebrochen: 'bg-amber-500',
};

export default function RecrawlTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [nichtLesbar, setNichtLesbar] = useState([]);
  const [retrying, setRetrying] = useState(null);
  const [filter, setFilter] = useState('alle'); // alle, offen, verarbeitet, beendet
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const pollRef = useRef(null);
  const token = localStorage.getItem('olympus_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/aktenuebernahme-v2/recrawl-status`, { headers });
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadNichtLesbar = async () => {
    try {
      const res = await fetch(`${API}/api/aktenuebernahme-v2/nicht-lesbar`, { headers });
      if (res.ok) {
        const data = await res.json();
        setNichtLesbar(data.dateien || []);
      }
    } catch (e) { console.error(e); }
  };

  const pollJobStatus = async () => {
    try {
      const res = await fetch(`${API}/api/aktenuebernahme-v2/recrawl-job-status`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'kein_job') {
          setJob(null);
          stopPolling();
        } else {
          setJob(data);
          if (data.status === 'fertig' || data.status === 'fehler' || data.status === 'abgebrochen') {
            stopPolling();
            loadStatus();
            if (data.status === 'fertig') toast.success('Re-Crawl abgeschlossen!');
          }
        }
      }
    } catch (e) { console.error(e); }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(pollJobStatus, 5000);
    pollJobStatus();
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    loadStatus();
    loadNichtLesbar();
    pollJobStatus();
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (job && (job.status === 'laeuft' || job.status === 'wartend') && !pollRef.current) {
      startPolling();
    }
  }, [job]);

  const startRecrawlKlient = async (klientId, name) => {
    if (!confirm(`Re-Crawl fuer "${name}" starten?`)) return;
    try {
      const res = await fetch(`${API}/api/aktenuebernahme-v2/recrawl-klient/${klientId}`, { method: 'POST', headers });
      const data = await res.json();
      if (data.error) { toast.error(data.error); }
      else { toast.success(`Re-Crawl gestartet: ${name}`); startPolling(); }
    } catch (e) { toast.error('Fehler: ' + e.message); }
  };

  const startRecrawlAlle = async () => {
    if (!confirm('Alle Akten im Hintergrund verarbeiten? Browser kann geschlossen werden.')) return;
    try {
      // force=true: Ignoriert Stichtag-Filter und bereits "fertig" markierte Klienten
      // Der User will EXPLIZIT alles neu crawlen wenn er den Button klickt.
      const res = await fetch(`${API}/api/aktenuebernahme-v2/recrawl-global?force=true`, { method: 'POST', headers });
      const data = await res.json();
      if (data.error) { toast.error(data.error); }
      else { toast.success(`Re-Crawl gestartet: ${data.klienten} Klienten`); startPolling(); }
    } catch (e) { toast.error('Fehler: ' + e.message); }
  };

  const stopRecrawl = async () => {
    if (!confirm('Re-Crawl abbrechen?')) return;
    await fetch(`${API}/api/aktenuebernahme-v2/recrawl-stop`, { method: 'POST', headers });
    toast.success('Wird gestoppt...');
    pollJobStatus();
  };

  const toggleAusblenden = async () => {
    const neu = !status?.ausgeblendet;
    await fetch(`${API}/api/aktenuebernahme-v2/recrawl-toggle?ausblenden=${neu}`, { method: 'POST', headers });
    loadStatus();
  };

  const retryDatei = async (dateiname, klientId) => {
    setRetrying(dateiname);
    try {
      const res = await fetch(`${API}/api/aktenuebernahme-v2/nicht-lesbar/retry?dateiname=${encodeURIComponent(dateiname)}&klient_id=${encodeURIComponent(klientId)}`, { method: 'POST', headers });
      const data = await res.json();
      if (data.status === 'geloest') {
        toast.success('Erfolgreich gelesen');
        setNichtLesbar(prev => prev.filter(d => !(d.dateiname === dateiname && d.klient_id === klientId)));
      } else { toast.error('Weiterhin nicht lesbar'); }
    } catch (e) { toast.error('Fehler'); }
    finally { setRetrying(null); }
  };

  const erledigtMarkieren = async (dateiname, klientId) => {
    await fetch(`${API}/api/aktenuebernahme-v2/nicht-lesbar?dateiname=${encodeURIComponent(dateiname)}&klient_id=${encodeURIComponent(klientId)}`, { method: 'DELETE', headers });
    setNichtLesbar(prev => prev.filter(d => !(d.dateiname === dateiname && d.klient_id === klientId)));
  };

  const isJobActive = job && (job.status === 'laeuft' || job.status === 'wartend');

  // Gefilterte und sortierte Klientenliste
  const filteredKlienten = useMemo(() => {
    if (!status?.klienten) return [];
    let list = status.klienten
      .filter(k => k.dateien > 0)
      .filter(k => {
        if (filter === 'offen') return k.fortschritt === 0;
        if (filter === 'verarbeitet') return k.fortschritt > 0;
        if (filter === 'beendet') return k.klient_status === 'beendet';
        return true;
      })
      .filter(k => {
        if (!search) return true;
        return k.name.toLowerCase().includes(search.toLowerCase());
      });

    // Sortierung
    list.sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case 'name': av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); return sortDir === 'asc' ? av.localeCompare(bv, 'de') : bv.localeCompare(av, 'de');
        case 'dateien': av = a.dateien || 0; bv = b.dateien || 0; break;
        case 'bereits_docs': av = a.bereits_docs || 0; bv = b.bereits_docs || 0; break;
        case 'fortschritt': av = a.fortschritt || 0; bv = b.fortschritt || 0; break;
        case 'klient_status': av = a.klient_status || ''; bv = b.klient_status || ''; return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        case 'institutionen': av = a.institutionen || 0; bv = b.institutionen || 0; break;
        case 'personen': av = a.personen || 0; bv = b.personen || 0; break;
        default: av = 0; bv = 0;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [status?.klienten, filter, search, sortBy, sortDir]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'name' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-500" /> : <ArrowDown className="w-3 h-3 text-blue-500" />;
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="recrawl-tab">
      {/* Laufender Job - prominent oben */}
      {job && job.status !== 'kein_job' && (
        <Card className={`border-2 ${isJobActive ? 'border-blue-400 bg-blue-50/30' : job.status === 'fertig' ? 'border-emerald-400 bg-emerald-50/30' : 'border-red-400 bg-red-50/30'}`} data-testid="active-job-card">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isJobActive ? <Loader2 className="w-5 h-5 text-blue-500 animate-spin" /> : job.status === 'fertig' ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                <span className="font-semibold text-lg">
                  {isJobActive ? 'Re-Crawl laeuft' : job.status === 'fertig' ? 'Re-Crawl abgeschlossen' : 'Re-Crawl ' + job.status}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">{job.klienten_fertig || 0} / {job.klienten_gesamt || 0} Klienten</span>
                {isJobActive && (
                  <Button variant="outline" size="sm" onClick={stopRecrawl} className="text-red-600 border-red-300" data-testid="stop-recrawl-btn">
                    <Square className="w-3.5 h-3.5 mr-1.5" /> Stopp
                  </Button>
                )}
              </div>
            </div>

            {/* Gesamt-Fortschrittsbalken */}
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-700 ${isJobActive ? 'bg-blue-500' : STATUS_COLORS[job.status] || 'bg-slate-400'}`}
                style={{ width: `${Math.max(2, Math.round((job.klienten_fertig || 0) / Math.max(job.klienten_gesamt, 1) * 100))}%` }}
              />
            </div>

            {/* Aktueller Klient */}
            {isJobActive && job.aktueller_klient && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />
                  <span className="font-medium">{job.aktueller_klient}</span>
                  <span className="text-slate-400">— {job.aktuelle_phase}</span>
                </div>
                {/* Detaillierter Fortschritt */}
                {job.detail && (
                  <div className="ml-6 text-xs text-slate-500 space-y-0.5">
                    {job.detail.dateien_gesamt > 0 && (
                      <div className="flex items-center gap-2">
                        <span>Dateien: {job.detail.dateien_verarbeitet || 0} / {job.detail.dateien_gesamt}</span>
                        {job.detail.ocr_skipped > 0 && (
                          <span className="text-emerald-600">({job.detail.ocr_skipped} aus Cache)</span>
                        )}
                      </div>
                    )}
                    {job.detail.aktuelle_datei && (
                      <div className="truncate max-w-md text-slate-400">{job.detail.aktuelle_datei}</div>
                    )}
                    {job.detail.phase_detail && (
                      <div className="text-blue-500">{job.detail.phase_detail}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Klienten-Status im Job */}
            {job.klienten && job.klienten.length > 1 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {job.klienten.map((k, i) => (
                  <div
                    key={i}
                    title={`${k.name}: ${k.status} (${k.fortschritt || 0}%)`}
                    className={`h-2 rounded-full transition-all ${STATUS_COLORS[k.status] || 'bg-slate-200'}`}
                    style={{ width: `${Math.max(8, 100 / job.klienten.length - 0.5)}%`, minWidth: '8px' }}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hauptkarte */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Akten Re-Crawl
            </span>
            <Button variant="ghost" size="sm" onClick={toggleAusblenden} data-testid="recrawl-toggle-btn">
              {status?.ausgeblendet ? <><Eye className="w-4 h-4 mr-1" /> Einblenden</> : <><EyeOff className="w-4 h-4 mr-1" /> Ausblenden</>}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Statistik-Kacheln */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <div className="bg-slate-50 rounded-lg p-2.5 text-center">
              <div className="text-xl font-bold text-slate-800">{status?.gesamt_klienten || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">Klienten</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2.5 text-center">
              <div className="text-xl font-bold text-slate-800">{status?.gesamt_dateien || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">Dateien</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
              <div className="text-xl font-bold text-emerald-700">{status?.gesamt_verarbeitet || 0}</div>
              <div className="text-[10px] text-emerald-600 uppercase tracking-wide">Verarbeitet</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-2.5 text-center">
              <div className="text-xl font-bold text-blue-700">{status?.klienten?.filter(k => k.fortschritt > 0).length || 0}</div>
              <div className="text-[10px] text-blue-600 uppercase tracking-wide">Gecrawlt</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-2.5 text-center">
              <div className="text-xl font-bold text-amber-700">{status?.klienten?.filter(k => k.fortschritt === 0 && k.dateien > 0).length || 0}</div>
              <div className="text-[10px] text-amber-600 uppercase tracking-wide">Offen</div>
            </div>
          </div>

          {/* Such- und Filter-Leiste */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Klient suchen..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                data-testid="recrawl-search"
              />
            </div>
            <div className="flex gap-1">
              {[
                { key: 'alle', label: 'Alle' },
                { key: 'offen', label: 'Offen' },
                { key: 'verarbeitet', label: 'Verarbeitet' },
                { key: 'beendet', label: 'Beendet' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                    filter === f.key
                      ? 'bg-slate-800 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Klientenliste */}
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="text-xs text-slate-500 uppercase tracking-wide">
                    <th className="text-left px-3 py-2.5 cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('name')}>
                      <span className="flex items-center gap-1">Klient <SortIcon col="name" /></span>
                    </th>
                    <th className="text-center px-2 py-2.5 w-16 cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('dateien')}>
                      <span className="flex items-center justify-center gap-1">Dateien <SortIcon col="dateien" /></span>
                    </th>
                    <th className="text-center px-2 py-2.5 w-20 cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('bereits_docs')}>
                      <span className="flex items-center justify-center gap-1">Docs <SortIcon col="bereits_docs" /></span>
                    </th>
                    <th className="text-center px-2 py-2.5 w-16 hidden sm:table-cell cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('institutionen')}>
                      <span className="flex items-center justify-center gap-1">Inst. <SortIcon col="institutionen" /></span>
                    </th>
                    <th className="text-center px-2 py-2.5 w-16 hidden sm:table-cell cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('personen')}>
                      <span className="flex items-center justify-center gap-1">Pers. <SortIcon col="personen" /></span>
                    </th>
                    <th className="px-2 py-2.5 w-32 cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('fortschritt')}>
                      <span className="flex items-center gap-1">Fortschritt <SortIcon col="fortschritt" /></span>
                    </th>
                    <th className="text-center px-2 py-2.5 w-16 cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('klient_status')}>
                      <span className="flex items-center justify-center gap-1">Status <SortIcon col="klient_status" /></span>
                    </th>
                    <th className="text-right px-3 py-2.5 w-20">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKlienten.map(k => {
                    const jobKlient = job?.klienten?.find(jk => jk.klient_id === k.id);
                    const isRunning = jobKlient?.status === 'laeuft';
                    const pct = isRunning ? (jobKlient?.fortschritt || 0) : k.fortschritt;

                    return (
                      <tr key={k.id} className={`border-t hover:bg-slate-50/80 ${isRunning ? 'bg-blue-50/30' : ''}`} data-testid={`recrawl-row-${k.id}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            {isRunning && <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin flex-shrink-0" />}
                            {jobKlient?.status === 'fertig' && <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                            {jobKlient?.status === 'fehler' && <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                            {k.ist_buero && <FolderOpen className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                            <span className="truncate max-w-[180px] font-medium">{k.name}</span>
                          </div>
                        </td>
                        <td className="text-center px-2 py-2 text-slate-600">{k.dateien}</td>
                        <td className="text-center px-2 py-2">
                          <span className={k.bereits_docs > 0 ? 'text-emerald-600 font-medium' : 'text-slate-400'}>{k.bereits_docs || 0}</span>
                          {k.umbenannt > 0 && <span className="text-[10px] text-blue-500 ml-0.5">({k.umbenannt})</span>}
                        </td>
                        <td className="text-center px-2 py-2 hidden sm:table-cell">
                          {k.institutionen > 0 ? <span className="text-purple-600">{k.institutionen}</span> : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="text-center px-2 py-2 hidden sm:table-cell">
                          {k.personen > 0 ? <span className="text-indigo-600">{k.personen}</span> : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all duration-500 ${
                                  isRunning ? 'bg-blue-500' : pct >= 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-500' : 'bg-slate-200'
                                }`}
                                style={{ width: `${Math.max(0, pct)}%` }}
                              />
                            </div>
                            <span className={`text-[11px] font-medium w-8 text-right ${pct >= 100 ? 'text-emerald-600' : pct > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                              {pct}%
                            </span>
                          </div>
                        </td>
                        <td className="text-center px-2 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            k.klient_status === 'aktiv' ? 'bg-emerald-100 text-emerald-700' :
                            k.klient_status === 'beendet' ? 'bg-slate-200 text-slate-600' :
                            'bg-amber-100 text-amber-700'
                          }`}>{k.klient_status || 'aktiv'}</span>
                        </td>
                        <td className="text-right px-3 py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2.5 text-xs"
                            disabled={isJobActive}
                            onClick={() => startRecrawlKlient(k.id, k.name)}
                            data-testid={`crawl-btn-${k.id}`}
                          >
                            <Play className="w-3 h-3 mr-1" /> Crawl
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredKlienten.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-slate-400">Keine Klienten gefunden</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Alle-Button */}
          <Button
            data-testid="start-global-recrawl-btn"
            onClick={startRecrawlAlle}
            disabled={isJobActive}
            className="w-full h-10 bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isJobActive
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Laeuft...</>
              : <><RefreshCw className="w-4 h-4 mr-2" /> Alle Klienten crawlen ({status?.gesamt_dateien || 0} Dateien)</>
            }
          </Button>
        </CardContent>
      </Card>

      {/* Nicht lesbare Dateien */}
      {nichtLesbar.length > 0 && (
        <Card data-testid="nicht-lesbar-card" className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-amber-600" />
              Nicht lesbare Dateien ({nichtLesbar.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {nichtLesbar.map((d, i) => (
                <div key={i} data-testid={`nicht-lesbar-item-${i}`} className="bg-white rounded-lg border p-2.5 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{d.dateiname}</div>
                    <div className="text-xs text-slate-500">{d.klient_name || d.klient_id}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={retrying === d.dateiname} onClick={() => retryDatei(d.dateiname, d.klient_id)}>
                      {retrying === d.dateiname ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RotateCcw className="w-3 h-3 mr-1" /> Retry</>}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-400 hover:text-red-500" onClick={() => erledigtMarkieren(d.dateiname, d.klient_id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
